// /app/frontend/src/pages/compliance/dsr/constants.js
// DSR Workflow Constants
export { JURISDICTION_OPTIONS } from '../../../constants/compliance';

// Tab configuration
export const DSR_TABS = [
  { id: 'request-info', label: 'Request Info', icon: 'FileText' },
  { id: 'requester', label: 'Requester', icon: 'User' },
  { id: 'verification', label: 'Verification', icon: 'Shield' },
  { id: 'data-search', label: 'Data Search', icon: 'Search' },
  { id: 'response', label: 'Response', icon: 'Send' },
  { id: 'review', label: 'Review', icon: 'ClipboardCheck' },
];

// Request type options
export const REQUEST_TYPES = [
  { value: 'access', label: 'Access Request', description: 'Get a copy of personal data' },
  { value: 'rectification', label: 'Rectification', description: 'Correct personal data' },
  { value: 'erasure', label: 'Erasure', description: 'Delete personal data' },
  { value: 'blocking', label: 'Blocking', description: 'Block processing (Jamaica/Cayman)' },
  { value: 'restriction', label: 'Restriction', description: 'Restrict processing' },
  { value: 'portability', label: 'Portability', description: 'Transfer data' },
  { value: 'objection', label: 'Objection', description: 'Object to processing' },
  { value: 'prevent_processing', label: 'Prevent Processing', description: 'Stop harmful use (Jamaica)' },
  { value: 'objection_marketing', label: 'Marketing Opt-Out', description: 'Stop marketing' },
  { value: 'automated_decision', label: 'Automated Decision', description: 'Human review' },
  { value: 'withdraw_consent', label: 'Withdraw Consent', description: 'Withdraw consent' },
  { value: 'complaint', label: 'Complaint', description: 'Data protection complaint' },
  { value: 'other', label: 'Other', description: 'Other request' },
];

export const RECEIVED_VIA_OPTIONS = [
  { value: 'email', label: 'Email' },
  // { value: 'letter', label: 'Letter' },        // TODO: not in backend enum
  { value: 'phone', label: 'Phone' },
  { value: 'post', label: 'Post' },
  { value: 'in_person', label: 'In Person' },
  { value: 'portal', label: 'Portal' },
  // { value: 'online_form', label: 'Online Form' },       // TODO: not in backend enum (use 'portal')
  // { value: 'social_media', label: 'Social Media' },     // TODO: not in backend enum
  // { value: 'third_party_agent', label: 'Third Party Agent' },  // TODO: not in backend enum
  // { value: 'regulator', label: 'Via Regulator' },       // TODO: not in backend enum
  { value: 'other', label: 'Other' },
];

export const VERIFICATION_METHODS = [
  { value: 'existing_account', label: 'Existing Customer Account' },
  { value: 'id_document', label: 'Government ID Document' },
  { value: 'knowledge_based', label: 'Knowledge-Based Questions' },
  { value: 'two_factor', label: 'Two-Factor Authentication' },
  { value: 'in_person', label: 'In-Person Verification' },
  { value: 'third_party_verification', label: 'Third Party Verification' },
  { value: 'notarised', label: 'Notarised Documentation' },
  { value: 'other', label: 'Other' },
];

export const VERIFICATION_GUIDANCE = {
  existing_account: "The individual has authenticated into a verified account associated with their personal data. Confirm the account is active and not shared with others.",
  id_document: "Confirm the ID document matches the name on the request. Record the document type in verification notes. Do not retain a copy unless required by policy.",
  knowledge_based: "At least two questions should have been answered correctly. Questions must relate to data already held by the organisation, not publicly available information.",
  two_factor: "Verify that the individual can access their registered authentication method. Confirm the method is still active and under their control.",
  in_person: "Meeting must be conducted with evidence of identity (government ID). Record the date, location, and method of identification used.",
  third_party_verification: "Verify the third party's identity and authority to act on behalf of the data subject. Retain copies of any power of attorney or authorisation documents.",
  notarised: "Confirm notarisation is current and the notary is registered in the relevant jurisdiction. Check that the signature matches identification provided.",
  other: "Document the verification method used in verification notes, including date and any supporting evidence retained.",
};

export const EXEMPTION_TYPES = [
  { value: 'national_security', label: 'National Security' },
  { value: 'crime_prevention', label: 'Crime Prevention/Law Enforcement' },
  { value: 'legal_proceedings', label: 'Legal Proceedings' },
  { value: 'regulatory_functions', label: 'Regulatory Functions' },
  { value: 'journalism_literature_art', label: 'Journalism/Literature/Art' },
  { value: 'research_statistics', label: 'Research/Statistics' },
  { value: 'legal_professional_privilege', label: 'Legal Professional Privilege' },
  { value: 'third_party_rights', label: 'Third Party Rights' },
  { value: 'confidential_references', label: 'Confidential References' },
  { value: 'other', label: 'Other' },
];

export const EXEMPTION_GUIDANCE = {
  national_security: "Rarely applicable to private sector organisations. Applies only where disclosure would harm national security interests. Seek legal advice before applying.",
  crime_prevention: "Applies where disclosure would prejudice crime prevention or law enforcement. Requires a real and specific risk, not a general concern.",
  legal_proceedings: "Applies to information held for the purposes of legal advice or proceedings. The organisation must be a party to the proceedings or anticipate them.",
  regulatory_functions: "Applies to information held for regulatory or supervisory functions. Usually relevant only to public sector or regulated entities.",
  journalism_literature_art: "Applies only to organisations whose principal activity is journalism, literature, or art. Requires balancing against data subject rights.",
  research_statistics: "Applies to research or statistical processing. Data must be processed for these purposes and not further processed in a way incompatible with them.",
  legal_professional_privilege: "Applies to communications protected by legal professional privilege. The document itself must be privileged, not merely related to a legal matter.",
  third_party_rights: "Applies where complying would require disclosing information about another individual. Consider whether redaction could allow partial compliance.",
  confidential_references: "Applies to references provided in confidence. The organisation must have promised confidentiality in advance.",
  other: "Document the exemption applied and the legal basis for the exemption in the response notes.",
};

export const DECISION_OPTIONS = [
  { value: 'granted_full', label: 'Granted in Full' },
  { value: 'granted_partial', label: 'Partially Granted' },
  { value: 'refused_exemption', label: 'Refused - Exemption Applies' },
  { value: 'refused_no_data', label: 'Refused - No Data Held' },
  { value: 'refused_id_not_verified', label: 'Refused - Identity Not Verified' },
  { value: 'refused_manifestly_unfounded', label: 'Refused - Manifestly Unfounded' },
  { value: 'refused_other', label: 'Refused - Other Reason' },
  { value: 'withdrawn', label: 'Withdrawn by Requester' },
];

export const RESPONSE_METHODS = [
  { value: 'email', label: 'Email' },
  { value: 'secure_portal', label: 'Secure Portal' },
  { value: 'encrypted_email', label: 'Encrypted Email' },
  { value: 'letter', label: 'Letter' },
  { value: 'in_person', label: 'In Person' },
  { value: 'registered_mail', label: 'Registered Mail' },
  { value: 'courier', label: 'Courier' },
];

export const STATUS_OPTIONS = [
  { value: 'received', label: 'Received' },
  { value: 'verifying', label: 'Verifying Identity' },
  { value: 'awaiting_info', label: 'Awaiting Info' },
  { value: 'processing', label: 'Processing' },
  { value: 'reviewing', label: 'Under Review' },
  { value: 'responded', label: 'Responded' },
  { value: 'closed', label: 'Closed' },
  { value: 'withdrawn', label: 'Withdrawn' },
  { value: 'refused', label: 'Refused' },
];

export const DEPARTMENTS = [
  { value: 'hr', label: 'Human Resources' },
  { value: 'it', label: 'IT/Technology' },
  { value: 'finance', label: 'Finance' },
  { value: 'legal', label: 'Legal' },
  { value: 'operations', label: 'Operations' },
  { value: 'customer_service', label: 'Customer Service' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'sales', label: 'Sales' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'other', label: 'Other' },
];

export const AGENT_RELATIONSHIPS = [
  { value: 'parent_guardian', label: 'Parent/Guardian' },
  { value: 'legal_representative', label: 'Legal Representative' },
  { value: 'power_of_attorney', label: 'Power of Attorney' },
  { value: 'executor', label: 'Executor/Administrator' },
  { value: 'solicitor', label: 'Solicitor/Lawyer' },
  { value: 'other', label: 'Other' },
];

export const EXTENSION_REASONS = [
  { value: 'complexity', label: 'Complexity of Request' },
  { value: 'volume', label: 'Volume of Data' },
  { value: 'multiple_requests', label: 'Multiple Requests' },
  { value: 'third_party_consultation', label: 'Third Party Consultation' },
  { value: 'other', label: 'Other' },
];

// Initial form data
export const DSR_INITIAL_FORM_DATA = {
  entity_id: '',
  organization_id: '',
  request_type: 'access',
  date_received: '',
  received_via: 'email',
  original_request_text: '',
  request_summary: '',
  specific_data_requested: '',
  requester: {
    name: '',
    email: '',
    phone: '',
    address: '',
    relationship: '',
    is_minor: false,
    is_agent: false,
    agent_name: '',
    agent_relationship: '',
    agent_authorization: false,
  },
  identity_verification: {
    verified: false,
    verification_date: '',
    verification_method: '',
    verification_notes: '',
    additional_info_requested: false,
    additional_info_details: '',
    additional_info_received_date: '',
  },
  deadline_info: {
    extension_applied: false,
    extension_reason: '',
    extended_deadline: '',
    extension_notification_sent: false,
    extension_notification_date: '',
  },
  data_search: {
    completed: false,
    completed_date: '',
    data_found: false,
    systems_searched: [],
    third_party_data_held: false,
    third_party_consultation_required: false,
  },
  response_info: {
    decision: '',
    decision_date: '',
    decision_rationale: '',
    exemption_applies: false,
    exemption_types: [],
    exemption_justification: '',
    partial_response: false,
    partial_response_reason: '',
    response_date: '',
    response_method: '',
    data_provided_summary: '',
    redactions_applied: false,
    redaction_reasons: '',
    appeal_rights_communicated: false,
    regulator_contact_provided: false,
  },
  assigned_to: '',
  departments_involved: [],
  priority: 'normal',
  notes: '',
  status: 'received',
  current_step: 1,
  jurisdiction: 'JM',
};

// Helper to find option label by value
export const getOptionLabel = (options, value) => {
  const opt = options.find(o => o.value === value);
  return opt ? opt.label : value;
};
